import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaFileAlt, FaPhoneAlt, FaWallet, FaArrowRight } from 'react-icons/fa';

gsap.registerPlugin(ScrollTrigger);

const SectionHowItWorks = () => {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    // GSAP scroll animation for cards
    cardsRef.current.forEach((card, index) => {
      gsap.fromTo(
        card,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
          delay: index * 0.15,
        }
      );
    });
  }, []);

  const steps = [
    {
      icon: FaFileAlt,
      step: '01',
      title: 'Apply Online',
      description: 'Complete our quick and easy online application form. It only takes a few minutes to get started.',
      color: 'from-primary to-primary-600',
    },
    {
      icon: FaPhoneAlt,
      step: '02',
      title: 'Quick Assessment',
      description: 'Our consultants will call you to complete a responsible lending assessment process.',
      color: 'from-primary-600 to-primary-700',
    },
    {
      icon: FaWallet,
      step: '03',
      title: 'Get Your Cash',
      description: 'Once approved, loan funds are deposited directly into your account. Sweet as!',
      color: 'from-secondary-500 to-secondary-600',
    },
  ];

  return (
    <section ref={sectionRef} className="section-padding bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent pointer-events-none"></div>
      
      <div className="container mx-auto px-4 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-block bg-secondary/20 text-primary font-semibold px-4 py-2 rounded-full text-sm mb-4">
            Simple Process
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-dark mb-4">
            How It <span className="text-gradient">Works</span>
          </h2>
          <p className="text-gray-600 text-lg">
            Getting a loan with AirCash is quick, simple, and hassle-free. Here's how it works.
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className="relative group"
            >
              {/* Connector Line (hidden on last item and mobile) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-[calc(50%+60px)] w-[calc(100%-60px)] h-0.5 bg-gradient-to-r from-gray-200 to-gray-100">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-300">
                    <FaArrowRight />
                  </div>
                </div>
              )}

              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group-hover:border-primary/20 relative overflow-hidden">
                {/* Step Number */}
                <span className="absolute top-6 right-6 text-6xl font-display font-bold text-gray-100 group-hover:text-primary/10 transition-colors">
                  {step.step}
                </span>

                {/* Icon */}
                <div className={`w-20 h-20 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <step.icon className="text-white text-3xl" />
                </div>

                {/* Content */}
                <h3 className="font-display text-xl font-bold text-dark mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>

                {/* Hover effect decoration */}
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href="/apply-now"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-700 text-white font-semibold py-4 px-8 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/30"
          >
            Start Your Application
            <FaArrowRight />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default SectionHowItWorks;
